import { Form, type FormikProps } from "formik";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { admin_cse } from "@/config/roles";
import { adminRequests } from "@/api/(requests)/admin";
import {
  Button,
  ButtonTypes,
  Checkbox,
  CustomForm,
  Modal,
  Text,
  TextTypes,
} from "@/component-library";
import { CloseIcon } from "@/components/Icons/CloseIcon";
import CloseOnOutsideClick from "@/components/Common/CloseOnOutsideClick/CloseOnOutsideClick";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import dayjs from "@/lib/helpers/dayjs";
import { FormFieldError } from "@/component-library/FormFields/FormFieldError/FormFieldError";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Hint } from "@/ui2/atoms/Hint";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";
import { isAuthorized, isUserAdmin } from "@/utils/authToken";
import type { RootState } from "@/lib/types/state";
import { showToast } from "@/providers/ToastProvider";
import { Switch2 } from "@/ui2/molecules/Switch";

import "./UniversalAccessManagementCard.styles.scss";

type UniversalAccessReason = {
  on_call: boolean;
  other: boolean;
  other_free_text: string;
};

type ProviderDetails = {
  is_universal_access_enabled?: boolean;
  is_universal_access_enabled_modified_by_name?: string | null;
  is_universal_access_enabled_modified_timestamp?: string | null;
  is_universal_access_enabled_reason?: UniversalAccessReason | null;
};

type Props = {
  providerDetails?: ProviderDetails;
};

type FormValues = {
  // virtual Yup field — not a real form input but used as a cross-field validation error
  atLeastOneOptionSelected?: string;
  on_call: boolean;
  other: boolean;
  other_free_text: string;
};

const initialValues: FormValues = {
  on_call: false,
  other: false,
  other_free_text: "",
};

const validationSchema = Yup.object().shape({
  atLeastOneOptionSelected: Yup.mixed().test(
    "at-least-one-checked",
    "At least one reason must be selected.",
    function () {
      const { on_call, other } = this.parent;

      return on_call || other;
    },
  ),
  on_call: Yup.boolean(),
  other: Yup.boolean(),
  other_free_text: Yup.string().when("other", ([other], schema) =>
    other
      ? schema
          .matches(/^[a-zA-Z0-9, ']+$/, "Special characters are not allowed.")
          .required("This is required if other is selected.")
      : schema,
  ),
});

export function UniversalAccessManagementCard({ providerDetails = {} }: Props) {
  const { providerId } = useParams<{ providerId: string }>();
  const profile = useSelector((state: RootState) => state.auth.profile);

  const [isProviderUniversalAccessEnabled, setIsProviderUniversalAccessEnabled] = useState(
    providerDetails.is_universal_access_enabled,
  );
  const [lastModifiedByName, setLastModifiedByName] = useState(
    providerDetails.is_universal_access_enabled_modified_by_name,
  );
  const [lastModifiedTimestamp, setLastModifiedTimestamp] = useState(
    providerDetails.is_universal_access_enabled_modified_timestamp,
  );
  const [lastModifiedReasons, setLastModifiedReasons] = useState(
    providerDetails.is_universal_access_enabled_reason,
  );

  const [showReasonModal, setShowReasonModal] = useState(false);

  const buildReasonList = () => {
    if (!isProviderUniversalAccessEnabled || !lastModifiedReasons) {
      return "";
    }

    const knownReasonDisplayMap: Record<string, string> = {
      on_call: "On-Call",
    };

    return Object.entries(lastModifiedReasons)
      .filter(([reason_key, reason_value]) => reason_key !== "other" && reason_value)
      .map(([reason_key, reason_value]) => {
        if (reason_key === "other_free_text") {
          return reason_value as string;
        } else if (knownReasonDisplayMap[reason_key]) {
          return knownReasonDisplayMap[reason_key];
        } else {
          return reason_key;
        }
      })
      .join(", ");
  };

  const buildAuditInformation = () => {
    try {
      if (!lastModifiedTimestamp && isUserAdmin()) {
        return "";
      } else if (!lastModifiedTimestamp && !isUserAdmin()) {
        return "Contact a supervisor to enable global access";
      }

      let auditInfo = `${
        isProviderUniversalAccessEnabled ? "Enabled" : "Disabled"
      } by ${lastModifiedByName} ${dayjs.utc(lastModifiedTimestamp).local().format("M/D/YYYY")}.`;

      if (isProviderUniversalAccessEnabled) {
        auditInfo += ` Reason: "${buildReasonList()}."`;
      }

      return auditInfo;
    } catch (e) {
      console.log(e);

      return "";
    }
  };

  const onToggleChange = async () => {
    const updatedIsProviderUniversalAccessEnabled = !isProviderUniversalAccessEnabled;

    try {
      if (!updatedIsProviderUniversalAccessEnabled) {
        const data = {
          is_universal_access_enabled: updatedIsProviderUniversalAccessEnabled,
          provider_id: providerId,
        };

        await adminRequests.set_provider_universal_access({ data });
        showToast([{ heading: "Universal access has been disabled.", variant: "success" }]);
        setLastModifiedByName(profile?.name);
        setLastModifiedTimestamp(dayjs().utc().toISOString());
        setIsProviderUniversalAccessEnabled(updatedIsProviderUniversalAccessEnabled);
      } else {
        setShowReasonModal(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const submitForm = async (formData: FormValues) => {
    const updatedIsProviderUniversalAccessEnabled = !isProviderUniversalAccessEnabled;

    try {
      const reason: UniversalAccessReason = {
        on_call: formData.on_call,
        other: formData.other,
        other_free_text: formData.other ? formData.other_free_text : "",
      };

      const data = {
        is_universal_access_enabled: updatedIsProviderUniversalAccessEnabled,
        provider_id: providerId,
        reason,
      };

      await adminRequests.set_provider_universal_access({ data });
      showToast([{ heading: "Universal access has been enabled.", variant: "success" }]);
      setLastModifiedByName(profile?.name);
      setLastModifiedTimestamp(dayjs().utc().toISOString());
      setLastModifiedReasons(reason);
      setIsProviderUniversalAccessEnabled(updatedIsProviderUniversalAccessEnabled);
      setShowReasonModal(false);
    } catch (e) {
      console.log(e);
    }
  };

  const closeModal = () => {
    setShowReasonModal(false);
  };

  const renderForm = (formikProps: FormikProps<FormValues>) => {
    const formikInputProps = {
      errors: formikProps.errors,
      inputClasses: "Input--sm",
      touched: formikProps.touched,
    };

    return (
      <Form>
        <div className="text-align-start">
          <div>
            <Checkbox
              checked={formikProps.values.on_call}
              label="On-Call"
              labelClassName=""
              name="on_call"
              onChange={() => {}}
              onClick={async () => {
                await formikProps.setFieldValue("on_call", !formikProps.values.on_call);
              }}
              testId="UniversalAccessReasonOnCall"
              value={formikProps.values.on_call}
            />
          </div>

          <div className="d-flex mt-1">
            <div className="pr-2 mt-2">
              <Checkbox
                checked={formikProps.values.other}
                label="Other"
                labelClassName=""
                name="other"
                onChange={() => {}}
                onClick={async () => {
                  if (formikProps.values.other) {
                    await formikProps.setFieldValue("other_free_text", "");
                  }

                  await formikProps.setFieldValue("other", !formikProps.values.other);
                }}
                testId="UniversalAccessReasonOther"
                value={formikProps.values.other}
              />
            </div>

            <FormTextInput
              {...formikInputProps}
              className="provider-universal-access-modal--other-free-text-input"
              customError={undefined}
              disabled={!formikProps.values.other}
              formikProps={formikProps}
              inputClassName=""
              name="other_free_text"
              placeholder="Specify"
              subTitle=""
              testId="UniversalAccessReasonOtherText"
              title=""
              triggerErrorOnChange
            />
          </div>
        </div>

        {formikProps.errors.atLeastOneOptionSelected && (
          <FormFieldError>{formikProps.errors.atLeastOneOptionSelected}</FormFieldError>
        )}

        {!formikProps.errors.atLeastOneOptionSelected && <div className="pt-4"></div>}
        <div className="d-flex justify-content-end mt-2">
          <Button
            buttonSize=""
            buttonType={ButtonTypes.primaryOutlineV2}
            className="mr-3"
            disabled={false}
            name=""
            onClick={closeModal}
            style={{ minWidth: 105 }}
            testId=""
            type="button"
          >
            Cancel
          </Button>

          <Button
            buttonSize=""
            buttonType={ButtonTypes.primaryV2}
            className=""
            disabled={false}
            name=""
            onClick={() => {}}
            style={{ minWidth: 105 }}
            testId=""
            type="submit"
            {...getAutomationDataAttr("UniversalAccessFormSubmit")}
          >
            Submit
          </Button>
        </div>
      </Form>
    );
  };

  return (
    <>
      {showReasonModal && (
        <Modal cardClassName="provider-universal-access-modal" className="" show={true}>
          <CloseOnOutsideClick setShowComponent={closeModal}>
            <div className="d-flex justify-content-end">
              <CloseIcon
                className="provider-universal-access-modal--close-icon"
                iconSize={ICON_SIZES.SMALL}
                iconType={ICON_TYPES.OUTLINE}
                onClick={closeModal}
              />
            </div>

            <div className="mx-3 mb-3">
              <div className="mb-4">
                <Text className="" color="" layout="" testId="" type={TextTypes.label}>
                  Choose a reason for enabling global access
                </Text>
              </div>

              <CustomForm
                initialValues={initialValues}
                onSubmit={submitForm}
                render={renderForm}
                validationSchema={validationSchema}
              />
            </div>
          </CloseOnOutsideClick>
        </Modal>
      )}

      <DashboardCard2 classNameChildren="tw:flex tw:flex-col tw:gap-2" title="Universal Access">
        <p>Enable access to on-call, crisis management, and global search workflows.</p>

        <Switch2
          data-testid="SetIsUniversalAccessEnabledToggle"
          isDisabled={!isAuthorized(admin_cse)}
          isSelected={isProviderUniversalAccessEnabled}
          name="set_is_universal_access_enabled_toggle"
          onChange={onToggleChange}
        >
          <Heading2 as="h3" size={1}>
            Universal Access
          </Heading2>
        </Switch2>

        <Hint className="tw:mt-2" data-testid="UniversalAccessAuditInformation">
          {buildAuditInformation()}
        </Hint>
      </DashboardCard2>
    </>
  );
}
